/**
 * Cliente HTTP del debriefing — Fase 7b.4.
 *
 * Espejo del cliente de briefing pero contra `/api/debriefings/*`.
 * Reusa `ApiResult` y `apiCall` exportados desde `briefing/api.ts`
 * — la mecánica HTTP es idéntica y sólo cambian los paths y los
 * tipos del payload.
 *
 * Reusa también `fetchBriefingMissions` para poblar el dropdown
 * CAMPAÑA/MISIÓN del debriefing (mismo endpoint, mismo shape).
 */

import type { Debriefing, DebriefingPayload } from '@51esc/shared';
import type { ApiResult } from '../briefing/api.js';

interface DebriefingResponseBody {
  debriefing: Debriefing;
}
interface DebriefingsListResponseBody {
  debriefings: Debriefing[];
}
interface ApiErrorBody {
  error?: string;
  code?: string;
}

/**
 * Wrapper de `fetch` paralelo al de `briefing/api.ts`. Se duplica
 * deliberadamente para que el módulo `debriefing` no dependa de
 * detalles internos de `briefing/api.ts` más allá del tipo
 * `ApiResult`. Si en el futuro se extrae a un `_shared/api-utils.ts`,
 * los dos llamadores convergen sin tocar la API pública.
 */
async function apiCall<T>(
  method: string,
  path: string,
  body?: unknown,
  unwrap?: (json: unknown) => T,
): Promise<ApiResult<T>> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'Content-Type': 'application/json' };
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    console.error('[api] network', method, path, err);
    return {
      ok: false,
      status: 0,
      code: 'network',
      message: 'Sin conexión con el servidor.',
    };
  }

  if (res.status === 204) {
    return { ok: true, data: undefined as T };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    return {
      ok: false,
      status: res.status,
      code: 'bad_response',
      message: 'Respuesta inválida del servidor.',
    };
  }

  if (!res.ok) {
    const e = json as ApiErrorBody;
    return {
      ok: false,
      status: res.status,
      code: e.code ?? 'unknown',
      message: e.error ?? `Error HTTP ${res.status}.`,
    };
  }

  return { ok: true, data: unwrap ? unwrap(json) : (json as T) };
}

// ─── Debriefings: CRUD ─────────────────────────────────────────────────

export async function createDebriefing(
  payload: DebriefingPayload,
): Promise<ApiResult<Debriefing>> {
  return apiCall<Debriefing>(
    'POST',
    '/api/debriefings',
    payload,
    (j) => (j as DebriefingResponseBody).debriefing,
  );
}

export async function listDebriefings(): Promise<ApiResult<Debriefing[]>> {
  return apiCall<Debriefing[]>(
    'GET',
    '/api/debriefings',
    undefined,
    (j) => (j as DebriefingsListResponseBody).debriefings,
  );
}

export async function getDebriefing(
  id: string,
): Promise<ApiResult<Debriefing>> {
  return apiCall<Debriefing>(
    'GET',
    `/api/debriefings/${encodeURIComponent(id)}`,
    undefined,
    (j) => (j as DebriefingResponseBody).debriefing,
  );
}

export async function updateDebriefing(
  id: string,
  payload: DebriefingPayload,
): Promise<ApiResult<Debriefing>> {
  return apiCall<Debriefing>(
    'PATCH',
    `/api/debriefings/${encodeURIComponent(id)}`,
    payload,
    (j) => (j as DebriefingResponseBody).debriefing,
  );
}

export async function deleteDebriefing(id: string): Promise<ApiResult<void>> {
  return apiCall<void>(
    'DELETE',
    `/api/debriefings/${encodeURIComponent(id)}`,
  );
}

// ─── Debriefings: fotos ────────────────────────────────────────────────

export async function uploadDebriefingPhotos(
  debriefingId: string,
  files: File[],
): Promise<ApiResult<Debriefing>> {
  const fd = new FormData();
  for (const f of files) {
    fd.append('photos', f, f.name);
  }
  return apiCall<Debriefing>(
    'POST',
    `/api/debriefings/${encodeURIComponent(debriefingId)}/photos`,
    fd,
    (j) => (j as DebriefingResponseBody).debriefing,
  );
}

export async function deleteDebriefingPhoto(
  debriefingId: string,
  photoId: string,
): Promise<ApiResult<void>> {
  return apiCall<void>(
    'DELETE',
    `/api/debriefings/${encodeURIComponent(debriefingId)}/photos/${encodeURIComponent(photoId)}`,
  );
}
